import { FC, JSX } from 'react';
import {
  Box,
  Step,
  StepButton,
  StepIndicator,
  Stepper,
  Typography,
} from '@mui/joy';
import CheckIcon from '@mui/icons-material/Check';
import BusinessIcon from '@mui/icons-material/Business';
import CloudIcon from '@mui/icons-material/Cloud';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { CompanyFormApi } from './useCompanyForm';

export const WizardStepper: FC<{ form: CompanyFormApi }> = ({
  form,
}): JSX.Element => {
  const { isEnterprise, activeStep, createdOrgId, goToStep } = form;
  // Locked once the org exists (nothing left to edit in Step 1).
  const disabled = !!createdOrgId;
  const steps = [
    {
      label: 'Organization Details',
      description: 'Basic company information',
      icon: <BusinessIcon fontSize="small" />,
    },
    {
      label: isEnterprise ? 'Infrastructure Configuration' : 'Review & Confirm',
      description: isEnterprise
        ? 'Subdomain, database, S3 and Cognito'
        : 'Verify details before creating',
      icon: isEnterprise ? (
        <CloudIcon fontSize="small" />
      ) : (
        <FactCheckIcon fontSize="small" />
      ),
    },
  ];

  return (
    <Stepper
      size="md"
      className="wizard-stepper"
      sx={{
        mb: { xs: 2, md: 3 },
        pt: 1,
        '--Stepper-verticalGap': '0.5rem',
        '--StepIndicator-size': '2.25rem',
        '--Step-connectorThickness': '3px',
        '--Step-connectorRadius': '4px',
        '--Step-connectorInset': '0.5rem',
        '--Step-connectorBg': 'var(--joy-palette-neutral-outlinedBorder)',
      }}
    >
      {steps.map((step, idx) => {
        const completed = idx < activeStep;
        const isActive = idx === activeStep;

        return (
          <Step
            key={step.label}
            orientation="vertical"
            indicator={
              <StepIndicator
                variant={isActive || completed ? 'solid' : 'outlined'}
                color={
                  completed ? 'success' : isActive ? 'primary' : 'neutral'
                }
                sx={{
                  boxShadow: isActive
                    ? '0 0 0 4px var(--joy-palette-primary-softBg)'
                    : 'none',
                  transition: 'box-shadow 160ms ease',
                }}
              >
                {completed ? <CheckIcon /> : step.icon}
              </StepIndicator>
            }
            sx={{
              '&::after': {
                bgcolor: completed
                  ? 'var(--joy-palette-success-solidBg)'
                  : 'var(--joy-palette-neutral-outlinedBorder)',
                height: 'var(--Step-connectorThickness)',
              },
            }}
          >
            <StepButton
              disabled={disabled}
              onClick={() => goToStep(idx as 0 | 1)}
              sx={{ textAlign: 'left' }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  level="title-sm"
                  sx={{
                    fontWeight: isActive ? 700 : 700,
                    color: isActive ? 'primary.plainColor' : 'text.primary',
                  }}
                >
                  Step {idx + 1}
                </Typography>
                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                  {step.label}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{ color: 'text.tertiary', mt: 0.25 }}
                >
                  {step.description}
                </Typography>
              </Box>
            </StepButton>
          </Step>
        );
      })}
    </Stepper>
  );
};

export default WizardStepper;
